/**
 * PWWCapital — Session Timeout Hook (Section 15)
 *
 * Tracks user inactivity. After `timeoutMinutes` of no activity,
 * calls `onTimeout()`. One minute before expiry, calls `onWarning()`.
 *
 * Activity events: mousemove, mousedown, keydown, touchstart, scroll, wheel.
 * Uses a single shared document-level listener (passive) to minimise overhead.
 */

import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];

/**
 * @param {object} opts
 * @param {number} opts.timeoutMinutes       - Inactivity threshold (default: 15)
 * @param {() => void} opts.onTimeout        - Called when session expires
 * @param {(secondsLeft: number) => void} [opts.onWarning] - Called at warningSeconds
 * @param {number} [opts.warningSeconds]     - How early to warn (default: 60)
 * @param {boolean} [opts.enabled]           - Enable/disable the timer (default: true)
 */
export function useSessionTimeout({
  timeoutMinutes = 15,
  onTimeout,
  onWarning,
  warningSeconds = 60,
  enabled = true,
} = {}) {
  const lastActivityRef = useRef(Date.now());
  const warningFiredRef = useRef(false);
  const intervalRef = useRef(null);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningFiredRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, resetTimer, { passive: true }),
    );

    intervalRef.current = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      const timeoutMs = timeoutMinutes * 60 * 1000;
      const remainingMs = timeoutMs - idleMs;
      const remainingSec = Math.ceil(remainingMs / 1000);

      if (remainingMs <= 0) {
        clearInterval(intervalRef.current);
        onTimeout?.();
      } else if (remainingSec <= warningSeconds && !warningFiredRef.current) {
        warningFiredRef.current = true;
        onWarning?.(remainingSec);
      }
    }, 5000); // check every 5 seconds

    return () => {
      clearInterval(intervalRef.current);
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, resetTimer),
      );
    };
  }, [enabled, timeoutMinutes, onTimeout, onWarning, warningSeconds, resetTimer]);

  return { resetTimer };
}
